// WhatsNewDialog — 「新機能の紹介」一覧 (新しい順、過去分も見返せる)。
// お知らせ (AnnounceEntryModal) とは別枠。開くボタンの未読の印と、入室直後の 1 回の自動表示 (useWhatsNewEntryAutoOpen)。
// 動画は自動再生しない (controls + preload="none" + poster)。スマホ・タブレットの
// 通信量を食わないため。ゴーストクリック対策は InviteDialog と同じ armed ガード。

import { useEffect, useId, useRef } from 'react';

import type { WhatsNewEntry } from '../domain/whatsNew.js';
import { resetArmedOnReopen, shouldAllowConfirmDialogClick } from './ConfirmDialog.js';
import { WHATS_NEW_ARCHIVE_URL } from './whatsNewEntries.js';

export interface WhatsNewDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** 新しい順。 */
  readonly entries: ReadonlyArray<WhatsNewEntry>;
  /** 今回の open で初めて見るもの (「新着」印)。 */
  readonly newIds: ReadonlySet<string>;
}

export const WhatsNewDialog = ({
  open,
  onClose,
  entries,
  newIds,
}: WhatsNewDialogProps): React.ReactNode => {
  const titleId = useId();
  const armedRef = useRef(false);
  const prevOpenRef = useRef(open);
  resetArmedOnReopen(armedRef, prevOpenRef, open);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const guard = (e: { detail: number }): boolean =>
    shouldAllowConfirmDialogClick(armedRef.current, e.detail);

  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-center p-4"
      onPointerDown={() => {
        armedRef.current = true;
      }}
    >
      <button
        type="button"
        aria-label="閉じる"
        tabIndex={-1}
        onClick={(e) => {
          if (guard(e)) onClose();
        }}
        className="-z-10 absolute inset-0 cursor-default bg-black/60"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="max-h-[90vh] w-full min-w-0 max-w-[640px] overflow-y-auto overscroll-contain rounded border border-white/10 bg-[var(--color-panel)] p-5 shadow-xl"
      >
        <h2 id={titleId} className="mb-3 text-base font-bold text-[var(--color-accent)]">
          新機能の紹介
        </h2>
        {entries.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">いまのところ紹介はありません。</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-5 p-0">
            {entries.map((entry) => (
              <li
                key={entry.id}
                data-testid="whats-new-entry"
                className="border-white/10 border-b pb-5 last:border-b-0 last:pb-0"
              >
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-[11px] text-[var(--color-text-muted)]">{entry.date}</span>
                  {newIds.has(entry.id) ? (
                    <span className="rounded-full bg-[var(--color-accent)] px-1.5 py-0.5 font-bold text-[9px] text-black leading-none">
                      新着
                    </span>
                  ) : null}
                </div>
                <h3 className="mb-2 text-sm font-bold text-white">{entry.title}</h3>
                {entry.videoUrl !== undefined ? (
                  // 横幅いっぱい・縦横比維持 (h-auto)。自動再生しない。音声なし素材なので muted。
                  <video
                    controls
                    muted
                    playsInline
                    preload="none"
                    {...(entry.posterUrl !== undefined ? { poster: entry.posterUrl } : {})}
                    src={entry.videoUrl}
                    className="mb-2 block h-auto w-full rounded bg-black"
                  />
                ) : entry.posterUrl !== undefined ? (
                  <img src={entry.posterUrl} alt="" className="mb-2 block h-auto w-full rounded" />
                ) : null}
                <p className="m-0 text-xs leading-relaxed text-[var(--color-text)]">{entry.body}</p>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <p className="m-0 flex flex-wrap items-center gap-x-2 text-[11px] text-[var(--color-text-muted)]">
            <span>これより前の更新は更新履歴にまとめています</span>
            <a
              href={WHATS_NEW_ARCHIVE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--color-accent)] underline underline-offset-2 hover:brightness-110"
            >
              過去のお知らせ
            </a>
          </p>
          <button
            type="button"
            onClick={(e) => {
              if (guard(e)) onClose();
            }}
            className="rounded border border-white/30 bg-[var(--color-bg-soft)] px-4 py-2 text-sm text-white hover:bg-[var(--color-bg)]"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};
